import React, { useEffect, useState } from "react";
import LoadingSpinner from "../../../../../common/components/LoadingSpinner";
import StimPreview from "../StimPreview";

const PREVIEW_SIZE = 300;
const PREVIEW_GAP = 12;

// same coercion the main stims table uses - the flag comes back as a bool, 0/1 or a string
function isClutter(value) {
    if (value === true) return true;
    if (value === false) return false;
    if (value === 1) return true;
    if (value === 0) return false;

    if (typeof value === "string") {
        const flag = value.trim().toLowerCase();
        if (["true", "1", "t", "yes", "y"].includes(flag)) return true;
        if (["false", "0", "f", "no", "n"].includes(flag)) return false;
    }

    return false;
}

function DetailRow({ label, value }) {
    return (
        <>
            <dt style={{ color: "rgb(99, 115, 129)", fontSize: 13 }}>{label}</dt>
            <dd style={{ margin: 0, fontSize: 14, wordBreak: "break-word" }}>
                {value === null || value === undefined || value === "" ? "-" : value}
            </dd>
        </>
    );
}

export default function IntegratedStimTable({ loading, data, addStims, pgStimCount, stims, stimPosition, updateStep, updatedStimList, updatedStimArray, setUpdateStep, searched }) {

    const [showDetails, setShowDetails] = useState(false)
    const [stim, setStim] = useState(false)
    const [filteredData, setFilteredData] = useState([])
    // { item, top, left } for the hovered row, null when nothing is hovered
    const [preview, setPreview] = useState(null)

    useEffect(() => {
        setFilteredData(data)
    }, [data])

    function updateStimList() {
        if (!stims || !stims.length) {
            return
        }

        const selectedIds = new Set(stims.map((selected) => selected.id))

        // One pass over the list, applied to the previous state. The old version called
        // setFilteredData once per selected stim, each time filtering the same
        // render-time array, so every call overwrote the last and only one stim was
        // ever removed.
        setFilteredData((prev) => prev.filter((item) => !selectedIds.has(item.id)))
    }

    useEffect(() => {
        updateStimList()
    }, [stims])

    // Anchored off the trigger's own rect and positioned fixed, so it lines up with the
    // button instead of drifting over it, and the table's scroll container can't clip it.
    function openPreview(event, item) {
        const rect = event.currentTarget.getBoundingClientRect()
        const maxLeft = window.innerWidth - PREVIEW_SIZE - PREVIEW_GAP
        const maxTop = window.innerHeight - PREVIEW_SIZE - PREVIEW_GAP

        setPreview({
            item,
            top: Math.max(PREVIEW_GAP, Math.min(rect.top, maxTop)),
            left: Math.max(PREVIEW_GAP, Math.min(rect.right + PREVIEW_GAP, maxLeft)),
        })
    }

    function closePreview() {
        setPreview(null)
    }

    function openDetails(item) {
        closePreview()
        setStim(item)
        setShowDetails(true)
    }

    if (loading) {
        return (
            <LoadingSpinner
                style={{ margin: "80px auto", position: "relative", top: "unset" }}
            />
        );
    }

    if (showDetails) {
        return (
            <article style={{ padding: "8px 4px" }}>
                <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16 }} title={stim.name}>{stim.name || "Stim"}</h3>
                    <button className="button-primary" onClick={() => setShowDetails(false)}>
                        back to results
                    </button>
                </header>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
                    <div style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
                        <StimPreview item={stim} width={PREVIEW_SIZE} height={PREVIEW_SIZE} />
                    </div>

                    <dl
                        style={{
                            margin: 0,
                            display: "grid",
                            gridTemplateColumns: "auto minmax(0, 1fr)",
                            columnGap: 20,
                            rowGap: 8,
                            alignItems: "baseline",
                            minWidth: 260,
                            flex: 1,
                        }}
                    >
                        <DetailRow label="Id" value={stim.id} />
                        <DetailRow label="Name" value={stim.name} />
                        <DetailRow label="Brand" value={stim.brand} />
                        <DetailRow label="Ad type" value={stim.ad_type_name || stim.ad_type} />
                        <DetailRow label="Ad type group" value={stim.ad_type_group_name || stim.ad_type_group} />
                        <DetailRow label="Language" value={stim.language} />
                        <DetailRow label="Country" value={stim.country} />
                        <DetailRow
                            label="Clutter"
                            value={stim.clutter === null || stim.clutter === undefined ? null : isClutter(stim.clutter) ? "Clutter" : "Test"}
                        />
                        <DetailRow label="Created" value={stim.created ? String(stim.created).slice(0, 10) : null} />
                        <DetailRow
                            label="Source"
                            value={stim.source ? (
                                <a href={stim.source} target="_blank" rel="noopener noreferrer">
                                    open in new tab
                                </a>
                            ) : null}
                        />
                    </dl>
                </div>
            </article>
        );
    }

    if (!filteredData.length) {
        return (
            <section style={{ display: "flex", justifyContent: "center", padding: "48px 0", color: "#888", fontSize: 14 }}>
                <p style={{ margin: 0 }}>{searched}</p>
            </section>
        );
    }

    return (
        <div>
            {/* overflow has to live on a wrapper - a display:table element ignores it. Capped
                below the 480px article in BuildPgMain so the search bar stays in view. */}
            <div style={{ maxHeight: "360px", overflow: "auto", width: "100%" }}>
                <table style={{ padding: 0, width: "100%", minWidth: "820px" }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "rgb(244, 246, 248)" }}>
                        <tr>
                            <th style={{ width: 60 }}>Select</th>
                            <th style={{ width: 70 }}>Id</th>
                            <th>Name</th>
                            <th style={{ width: 110 }}>Details</th>
                            <th style={{ width: 110 }}>Preview</th>
                            <th style={{ width: 130 }}>Brand</th>
                            <th style={{ width: 110 }}>Language</th>
                            <th style={{ width: 110 }}>Country</th>
                            <th>Ad Type Group</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item) => (
                            <tr key={item.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            visibility: "visible",
                                            display: "inline-block",
                                            cursor: "pointer",
                                        }}
                                        onChange={() => {

                                            if (!updateStep) {
                                                addStims(item.id, stimPosition)
                                            }
                                            else {
                                                updatedStimArray(item.id, stimPosition,)
                                            }
                                        }
                                        }
                                    />
                                </td>
                                <td>{item.id}</td>
                                <td
                                    title={item.name}
                                    style={{ maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >{item.name}</td>
                                <td>
                                    <button className="button-primary" onClick={() => openDetails(item)}>
                                        details
                                    </button>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="button-primary"
                                        onMouseEnter={(event) => openPreview(event, item)}
                                        onMouseLeave={closePreview}
                                        onFocus={(event) => openPreview(event, item)}
                                        onBlur={closePreview}
                                    >
                                        show stim
                                    </button>
                                </td>
                                <td>{item.brand || "-"}</td>
                                <td>{item.language || "-"}</td>
                                <td>{item.country || "-"}</td>
                                <td>{item.ad_type_group_name || item.ad_type_group || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {preview ? (
                <div
                    style={{
                        position: "fixed",
                        top: preview.top,
                        left: preview.left,
                        zIndex: 1400,
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 6,
                        overflow: "hidden",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.24)",
                        pointerEvents: "none",
                    }}
                >
                    <StimPreview item={preview.item} width={PREVIEW_SIZE} height={PREVIEW_SIZE} />
                </div>
            ) : null}
        </div>
    );
};
